import { Anchor, Icon } from '@dnb/eufemia';
import { chevron_down_medium as ChevronDownIcon, chevron_right_medium as ChevronRightIcon } from '@dnb/eufemia/icons';
import classnames from 'classnames';
import { JSX, useEffect, useMemo, useState } from 'react';

import { useScrollSpy } from '@/hooks/useScrollSpy';

import { type FlattenedItem, Search } from './Search';

import style from './index.module.css';

export type NavigationItem = {
  displayName: string;
  slug: string;
  children: NavigationItem[];
};

interface Props {
  items: NavigationItem[];
}

function flattenSlugs(items: NavigationItem[]): string[] {
  return items.flatMap((item) => [item.slug, ...flattenSlugs(item.children)]);
}

function hasActiveDescendant(item: NavigationItem, isActiveFn: (slug: string) => boolean): boolean {
  return isActiveFn(item.slug) || item.children.some((child) => hasActiveDescendant(child, isActiveFn));
}

interface MenuItemsProps {
  items: NavigationItem[];
  isActiveFunction: (slug: string) => boolean;
  expandedSections: Set<string>;
  toggleMenuSection: (slug: string) => void;
  depth?: number;
}

function MenuItems({
  items,
  isActiveFunction,
  expandedSections,
  toggleMenuSection,
  depth = 0,
}: MenuItemsProps): JSX.Element {
  return (
    <ul className={style['MenuItems']}>
      {items.map((item) => {
        const hasChildren = item.children.length > 0;
        const isParentActive = hasActiveDescendant(item, isActiveFunction);
        // Sections with an active descendant are expanded by default.
        // `expandedSections` tracks manual toggles away from that default state,
        // so XOR (`!==`) means "use the default unless the user toggled it".
        const isExpanded = isParentActive !== expandedSections.has(item.slug);
        return (
          <li className={hasChildren ? style['ExpandableRow'] : undefined} key={item.slug}>
            <Anchor className={classnames(style['MenuItem'], isParentActive && style['Active'])} href={`#${item.slug}`}>
              <span className={style['MenuItemText']} style={{ marginLeft: `${1.5 * depth}rem` }}>
                {item.displayName}
              </span>
            </Anchor>
            {hasChildren && (
              <button
                aria-expanded={isExpanded}
                aria-label={isExpanded ? `Collapse ${item.displayName}` : `Expand ${item.displayName}`}
                className={classnames(style['ChevronToggle'], isParentActive && style['Active'])}
                onClick={() => toggleMenuSection(item.slug)}
                type="button"
              >
                <Icon color="var(--color-sea-green)" icon={isExpanded ? ChevronDownIcon : ChevronRightIcon} />
              </button>
            )}
            {hasChildren && isExpanded && (
              <MenuItems
                depth={depth + 1}
                expandedSections={expandedSections}
                isActiveFunction={isActiveFunction}
                items={item.children}
                toggleMenuSection={toggleMenuSection}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function NavigationMenu({ items }: Props) {
  const [searchResults, setSearchResults] = useState<FlattenedItem[] | undefined>(undefined);

  const allSlugs = useMemo(() => flattenSlugs(items), [items]);
  const activeSlug = useScrollSpy(allSlugs);

  const [expandedMenuSections, setExpandedMenuSections] = useState<Set<string>>(new Set());

  const toggleMenuSection = (slug: string) => {
    setExpandedMenuSections((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  const isActive = (itemSlug: string): boolean => activeSlug === itemSlug;

  useEffect(() => {
    setExpandedMenuSections(new Set());
  }, [activeSlug]);

  useEffect(() => {
    if (items)
      setTimeout(() => {
        document.getElementById(globalThis.location.hash.replace('#', ''))?.scrollIntoView({ behavior: 'auto' });
      }, 500);
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!searchResults) return items;

    return searchResults.map((result) => ({
      displayName: result.displayName,
      slug: result.slug,
      children: [],
    }));
  }, [items, searchResults]);

  return (
    <>
      <Search items={items} onSearchResult={setSearchResults} />
      <MenuItems
        expandedSections={expandedMenuSections}
        isActiveFunction={isActive}
        items={filteredItems}
        toggleMenuSection={toggleMenuSection}
      />
    </>
  );
}
